import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation as useRouterLocation } from 'react-router-dom';
import { ShoppingCart, MapPin, ChevronDown, ArrowLeft, RotateCcw, Target } from 'lucide-react';
import { useLocation } from '../../contexts/LocationContext';
import { useCart } from '../../contexts/CartContext';
import { useCustomer } from '../../contexts/CustomerContext';
import ReorderModal from '../order/ReorderModal';

export default function ShopNavbar() {
  const { selectedLocation, setShowLocationPicker } = useLocation();
  const { itemCount, total, lastAddedAt } = useCart();
  const { loyaltyBalance, customer } = useCustomer();
  const routerLocation = useRouterLocation();
  const [showReorder, setShowReorder] = useState(false);
  const [bouncing, setBouncing] = useState(false);
  const cartRef = useRef(null);

  useEffect(() => {
    if (lastAddedAt > 0) {
      setBouncing(true);
      const timer = setTimeout(() => setBouncing(false), 400);
      return () => clearTimeout(timer);
    }
  }, [lastAddedAt]);

  const isCheckout = routerLocation.pathname.includes('/checkout');
  const isConfirmation = routerLocation.pathname.includes('/bestaetigung');

  const formattedTotal = '€' + total.toFixed(2).replace('.', ',');

  return (
    <nav className="shop-navbar fixed top-0 left-0 right-0 z-50 h-[60px]" data-testid="shop-navbar">
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        {/* Left: Logo + Back */}
        <div className="flex items-center gap-3">
          {(isCheckout || isConfirmation) ? (
            <Link
              to={selectedLocation ? `/bestellen/${selectedLocation.slug}` : '/bestellen'}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              data-testid="shop-back-link"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Zurück zur Karte</span>
            </Link>
          ) : (
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="shop-logo-link">
              <img
                src="/images/logo-icon.png"
                alt="ZOZO Burger"
                className="w-10 h-10 object-contain"
              />
              <span className="hidden sm:block font-display font-bold text-white tracking-wide text-xl">
                ZOZO
              </span>
            </Link>
          )}
        </div>

        {/* Center: Location with MapPin */}
        <button
          onClick={() => setShowLocationPicker(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            selectedLocation
              ? 'text-white/80 hover:text-white hover:bg-white/5'
              : 'text-[#A81C1C] bg-[#A81C1C]/10 hover:bg-[#A81C1C]/15 animate-subtle-pulse'
          }`}
          data-testid="shop-location-selector"
        >
          <MapPin className="w-4 h-4 text-[#A81C1C]" />
          <span className="max-w-[160px] truncate font-semibold">
            {selectedLocation ? selectedLocation.name?.replace('ZOZO Burger ', '') : 'Standort wählen'}
          </span>
          <ChevronDown className="w-3.5 h-3.5 opacity-50" />
        </button>

        {/* Right: Points + Reorder + Cart */}
        <div className="flex items-center gap-2">
          {customer && loyaltyBalance > 0 && !isCheckout && !isConfirmation && (
            <Link
              to="/meine-bestellungen"
              className="flex items-center gap-1.5 px-3 py-2 text-white/70 hover:text-white hover:bg-white/5 rounded-lg text-xs font-medium transition-all"
              data-testid="shop-points-badge"
            >
              <Target className="w-3.5 h-3.5 text-[#A81C1C]" />
              <span>{loyaltyBalance} Punkte</span>
            </Link>
          )}
          {!isCheckout && !isConfirmation && (
            <button
              onClick={() => setShowReorder(true)}
              className="flex items-center gap-1.5 px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/5 rounded-lg text-sm font-medium transition-all"
              data-testid="shop-reorder-btn"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="hidden md:inline">Nachbestellen</span>
            </button>
          )}
          {!isConfirmation && (
            <Link
              to="/bestellen/checkout"
              ref={cartRef}
              className={`relative flex items-center gap-2 px-5 py-2.5 bg-[#A81C1C] hover:bg-[#8B1616] text-white rounded-lg font-bold text-sm transition-all duration-200 hover:shadow-[0_4px_14px_0_rgba(168,28,28,0.35)] hover:-translate-y-px ${bouncing ? 'cart-add-bounce' : ''}`}
              data-testid="shop-cart-button"
            >
              <ShoppingCart className="w-[18px] h-[18px]" />
              <span className="hidden sm:inline">{formattedTotal}</span>
              {itemCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-amber-400 text-neutral-900 text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                  {itemCount}
                </span>
              )}
            </Link>
          )}
        </div>
      </div>

      <ReorderModal isOpen={showReorder} onClose={() => setShowReorder(false)} />
    </nav>
  );
}
